import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      return await res.json();
    } catch (err) { return {}; }
  }
};

function TodayAIUsagePage({ token, user }) {
  const { language } = useLanguage();
  const [usageData, setUsageData] = useState(null);
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [budgetSettings, setBudgetSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [newBudget, setNewBudget] = useState(500);

  const loadTodayUsage = async () => {
    try {
      setRefreshing(true);
      const schoolId = user?.school_id || 'default_school';
      const response = await api.get(`/api/ai-usage/today/${schoolId}`, token);
      setUsageData(response);
    } catch (err) {
      console.error('Error loading today usage:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadBudgetStatus = async () => {
    try {
      const schoolId = user?.school_id || 'default_school';
      const [statusRes, settingsRes] = await Promise.all([
        api.get(`/api/ai-usage/budget/status/school/${schoolId}`, token),
        api.get(`/api/ai-usage/budget/school/${schoolId}`, token)
      ]);
      setBudgetStatus(statusRes);
      setBudgetSettings(settingsRes?.settings);
      setNewBudget(settingsRes?.settings?.monthly_budget || 500);
    } catch (err) {
      console.error('Error loading budget status:', err);
    }
  };

  const saveBudgetSettings = async () => {
    try {
      const schoolId = user?.school_id || 'default_school';
      await api.post(`/api/ai-usage/budget/school/${schoolId}`, {
        monthly_budget: parseFloat(newBudget),
        alert_threshold_80: true,
        alert_threshold_100: true,
        email_alerts: true,
        dashboard_alerts: true
      }, token);
      setShowBudgetModal(false);
      loadBudgetStatus();
    } catch (err) {
      console.error('Error saving budget:', err);
    }
  };

  useEffect(() => {
    loadTodayUsage();
    loadBudgetStatus();
    // Auto refresh every 30 seconds
    const interval = setInterval(() => {
      loadTodayUsage();
      loadBudgetStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const featureNames = {
    'mcq_generator': 'MCQ Generator',
    'question_paper': 'Question Paper',
    'notice_simplifier': 'Notice Simplifier',
    'fee_reminder': 'Fee Reminder',
    'progress_summary': 'Progress Summary',
    'complaint_reply': 'Complaint Reply',
    'complaint_guidance': 'Complaint Guidance',
    'faq_bot': 'FAQ Bot',
    'homework_assistant': 'Homework Assistant',
    'homework_hints': 'Homework Hints',
    'compliance_checklist': 'Compliance Checklist',
    'study_plan': 'Study Plan',
    'practice_tests': 'Practice Tests',
    'chapter_explanation': 'Chapter Explanation',
    'caption_generator': 'Caption Generator',
    'hashtag_generator': 'Hashtag Generator'
  };

  const tierColors = {
    1: 'bg-green-100 text-green-700',
    2: 'bg-blue-100 text-blue-700',
    3: 'bg-purple-100 text-purple-700'
  };

  const tierNames = {
    1: 'Tier 1 (Gemini)',
    2: 'Tier 2 (GPT-4o)',
    3: 'Tier 3 (GPT-5.2)'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="today-ai-usage-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {language === 'hi' ? '📈 आज का AI उपयोग' : '📈 Today\'s AI Usage'}
          </h2>
          <p className="text-gray-500 text-sm">
            {language === 'hi' ? `तारीख: ${usageData?.date || '-'}` : `Date: ${usageData?.date || '-'}`}
          </p>
        </div>
        <button
          onClick={loadTodayUsage}
          disabled={refreshing}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
          data-testid="refresh-usage-btn"
        >
          {refreshing ? (
            <span className="animate-spin">🔄</span>
          ) : (
            '🔄'
          )}
          {language === 'hi' ? 'रीफ्रेश' : 'Refresh'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-5" data-testid="today-total-calls">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल कॉल्स' : 'Total Calls'}</p>
          <p className="text-3xl font-bold text-gray-800">{usageData?.summary?.total_calls || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5" data-testid="today-total-cost">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल लागत' : 'Total Cost'}</p>
          <p className="text-3xl font-bold text-green-600">₹{usageData?.summary?.total_cost_inr || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'टियर वितरण' : 'Tier Distribution'}</p>
          <div className="flex gap-2 mt-2">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
              T1: {usageData?.summary?.by_tier?.[1] || 0}
            </span>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
              T2: {usageData?.summary?.by_tier?.[2] || 0}
            </span>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
              T3: {usageData?.summary?.by_tier?.[3] || 0}
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <p className="text-gray-500 text-sm">{language === 'hi' ? 'सबसे ज्यादा उपयोग' : 'Most Used'}</p>
          <p className="text-lg font-semibold text-gray-700 mt-1">
            {usageData?.summary?.by_feature ? 
              Object.entries(usageData.summary.by_feature)
                .sort((a, b) => b[1].calls - a[1].calls)[0]?.[0]?.replace(/_/g, ' ') || '-'
              : '-'
            }
          </p>
        </div>
      </div>

      {/* Budget Status Card */}
      {budgetStatus && (
        <div className={`rounded-xl shadow overflow-hidden ${
          budgetStatus.status === 'exceeded' ? 'bg-red-50 border-2 border-red-400' :
          budgetStatus.status === 'warning' ? 'bg-yellow-50 border-2 border-yellow-400' :
          'bg-white'
        }`} data-testid="budget-status-card">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              {budgetStatus.status === 'exceeded' ? '🚨' : budgetStatus.status === 'warning' ? '⚠️' : '💰'}
              {language === 'hi' ? 'मासिक AI बजट' : 'Monthly AI Budget'}
            </h3>
            <button
              onClick={() => setShowBudgetModal(true)}
              className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
              data-testid="edit-budget-btn"
            >
              {language === 'hi' ? '✏️ बजट बदलें' : '✏️ Edit Budget'}
            </button>
          </div>
          <div className="p-4">
            {/* Alert Messages */}
            {budgetStatus.alerts?.length > 0 && (
              <div className="mb-4 space-y-2">
                {budgetStatus.alerts.map((alert, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${
                    alert.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <p className="font-medium">{language === 'hi' ? alert.message : alert.message_en}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Budget Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">
                  {language === 'hi' ? 'उपयोग' : 'Usage'}: ₹{budgetStatus.budget?.current_usage || 0}
                </span>
                <span className="text-sm text-gray-600">
                  {language === 'hi' ? 'बजट' : 'Budget'}: ₹{budgetStatus.budget?.monthly_limit || 500}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full transition-all duration-500 ${
                    budgetStatus.budget?.percentage_used >= 100 ? 'bg-red-500' :
                    budgetStatus.budget?.percentage_used >= 80 ? 'bg-yellow-500' :
                    budgetStatus.budget?.percentage_used >= 50 ? 'bg-orange-400' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, budgetStatus.budget?.percentage_used || 0)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">{budgetStatus.budget?.percentage_used?.toFixed(1)}% {language === 'hi' ? 'उपयोग' : 'used'}</span>
                <span className="text-xs text-gray-500">
                  {language === 'hi' ? 'शेष' : 'Remaining'}: ₹{budgetStatus.budget?.remaining || 0}
                </span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-800">{budgetStatus.budget?.total_calls || 0}</p>
                <p className="text-xs text-gray-500">{language === 'hi' ? 'इस महीने कॉल्स' : 'Calls This Month'}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">₹{budgetStatus.budget?.current_usage || 0}</p>
                <p className="text-xs text-gray-500">{language === 'hi' ? 'खर्च' : 'Spent'}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">₹{budgetStatus.projection?.daily_average || 0}</p>
                <p className="text-xs text-gray-500">{language === 'hi' ? 'दैनिक औसत' : 'Daily Avg'}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">₹{budgetStatus.projection?.projected_monthly || 0}</p>
                <p className="text-xs text-gray-500">{language === 'hi' ? 'अनुमानित मासिक' : 'Projected Monthly'}</p>
              </div>
            </div>

            {/* Projection Warning */}
            {budgetStatus.projection?.projected_monthly > budgetStatus.budget?.monthly_limit && (
              <div className="mt-4 p-3 bg-orange-100 rounded-lg text-orange-800 text-sm">
                📊 {language === 'hi' 
                  ? `वर्तमान दर पर आपका अनुमानित मासिक खर्च ₹${budgetStatus.projection?.projected_monthly} होगा, जो आपके ₹${budgetStatus.budget?.monthly_limit} के बजट से अधिक है।`
                  : `At current rate, your projected monthly spend will be ₹${budgetStatus.projection?.projected_monthly}, which exceeds your ₹${budgetStatus.budget?.monthly_limit} budget.`
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* Budget Settings Modal */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" data-testid="budget-modal">
            <h3 className="text-xl font-bold mb-4">
              {language === 'hi' ? '💰 मासिक AI बजट सेट करें' : '💰 Set Monthly AI Budget'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'hi' ? 'मासिक बजट (₹)' : 'Monthly Budget (₹)'}
                </label>
                <input
                  type="number"
                  value={newBudget}
                  onChange={(e) => setNewBudget(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  min="100"
                  step="50"
                  data-testid="budget-input"
                />
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                <p className="font-medium mb-2">{language === 'hi' ? '💡 सुझाव:' : '💡 Suggestions:'}</p>
                <ul className="space-y-1 text-xs">
                  <li>• {language === 'hi' ? 'छोटे स्कूल (100 students): ₹200-500/month' : 'Small school (100 students): ₹200-500/month'}</li>
                  <li>• {language === 'hi' ? 'मध्यम स्कूल (500 students): ₹500-1500/month' : 'Medium school (500 students): ₹500-1500/month'}</li>
                  <li>• {language === 'hi' ? 'बड़े स्कूल (1000+ students): ₹1500-5000/month' : 'Large school (1000+ students): ₹1500-5000/month'}</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBudgetModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {language === 'hi' ? 'रद्द करें' : 'Cancel'}
                </button>
                <button
                  onClick={saveBudgetSettings}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  data-testid="save-budget-btn"
                >
                  {language === 'hi' ? 'सेव करें' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature-wise Summary */}
      {usageData?.summary?.by_feature && Object.keys(usageData.summary.by_feature).length > 0 && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="p-4 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'फीचर अनुसार उपयोग' : 'Usage by Feature'}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="feature-summary-table">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'फीचर' : 'Feature'}</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'कॉल्स' : 'Calls'}</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'लागत' : 'Cost'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(usageData.summary.by_feature).map(([feature, data]) => (
                  <tr key={feature} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{featureNames[feature] || feature.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-center text-sm">{data.calls}</td>
                    <td className="px-4 py-3 text-center text-sm text-green-600">₹{data.cost?.toFixed(2) || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed Usage Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'विस्तृत लॉग' : 'Detailed Log'}</h3>
          <span className="text-sm text-gray-500">
            {language === 'hi' ? `${usageData?.records?.length || 0} रिकॉर्ड्स` : `${usageData?.records?.length || 0} records`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="usage-detail-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'समय' : 'Time'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'फीचर' : 'Feature'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'hi' ? 'मॉडल' : 'Model'}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'टियर' : 'Tier'}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'लागत' : 'Cost'}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'रोल' : 'Role'}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">{language === 'hi' ? 'समय (ms)' : 'Time (ms)'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {usageData?.records?.length > 0 ? (
                usageData.records.map((record, idx) => (
                  <tr key={record.id || idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono">{record.time}</td>
                    <td className="px-4 py-3 text-sm">{featureNames[record.feature] || record.feature?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{record.model}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${tierColors[record.tier] || 'bg-gray-100'}`}>
                        T{record.tier}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-green-600">₹{record.cost}</td>
                    <td className="px-4 py-3 text-center text-sm capitalize">{record.user_role}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-500">{record.response_time_ms}ms</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    {language === 'hi' ? 'आज कोई AI कॉल नहीं हुई' : 'No AI calls today'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <div className="flex gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <p className="font-medium text-blue-800">{language === 'hi' ? 'जानकारी' : 'Info'}</p>
            <p className="text-sm text-blue-600 mt-1">
              {language === 'hi' 
                ? 'यह पेज हर 30 सेकंड में ऑटो-रीफ्रेश होता है। सभी AI कॉल्स यहाँ दिखाई जाती हैं।'
                : 'This page auto-refreshes every 30 seconds. All AI calls are logged here.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Settings Page with Integrations and School Profile
function TransportPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(null); // 'vehicle', 'route', 'driver'
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, vehiclesRes, routesRes, driversRes] = await Promise.all([
        api.get('/api/transport/dashboard', token),
        api.get('/api/transport/vehicles', token),
        api.get('/api/transport/routes', token),
        api.get('/api/transport/drivers', token)
      ]);
      setData(dashRes);
      setVehicles(vehiclesRes.vehicles || []);
      setRoutes(routesRes.routes || []);
      setDrivers(driversRes.drivers || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleAddVehicle = async () => {
    setSaving(true);
    try {
      await api.post('/api/transport/vehicles/simple', formData, token);
      setShowAddModal(null);
      setFormData({});
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleAddRoute = async () => {
    setSaving(true);
    try {
      await api.post('/api/transport/routes/simple', formData, token);
      setShowAddModal(null);
      setFormData({});
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  const handleAddDriver = async () => {
    setSaving(true);
    try {
      await api.post('/api/transport/drivers/simple', formData, token);
      setShowAddModal(null);
      setFormData({});
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  };
  
  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div></div>;
  
  return (
    <div className="space-y-6" data-testid="transport-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">🚌 {language === 'hi' ? 'परिवहन प्रबंधन' : 'Transport Management'}</h1>
        <div className="flex gap-2">
          <button onClick={() => { setShowAddModal('vehicle'); setFormData({}); }} className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm" data-testid="add-vehicle-btn">
            + {language === 'hi' ? 'वाहन' : 'Vehicle'}
          </button>
          <button onClick={() => { setShowAddModal('route'); setFormData({}); }} className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm" data-testid="add-route-btn">
            + {language === 'hi' ? 'मार्ग' : 'Route'}
          </button>
          <button onClick={() => { setShowAddModal('driver'); setFormData({}); }} className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm" data-testid="add-driver-btn">
            + {language === 'hi' ? 'ड्राइवर' : 'Driver'}
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => setActiveTab('vehicles')}>
          <p className="text-3xl font-bold text-blue-600">{data?.summary?.total_vehicles || 0}</p>
          <p className="text-sm text-gray-500">{language === 'hi' ? 'वाहन' : 'Vehicles'}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => setActiveTab('routes')}>
          <p className="text-3xl font-bold text-green-600">{data?.summary?.total_routes || 0}</p>
          <p className="text-sm text-gray-500">{language === 'hi' ? 'मार्ग' : 'Routes'}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => setActiveTab('drivers')}>
          <p className="text-3xl font-bold text-purple-600">{data?.summary?.total_drivers || 0}</p>
          <p className="text-sm text-gray-500">{language === 'hi' ? 'ड्राइवर' : 'Drivers'}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <p className="text-3xl font-bold text-orange-600">{data?.summary?.total_students || 0}</p>
          <p className="text-sm text-gray-500">{language === 'hi' ? 'छात्र' : 'Students'}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {['dashboard', 'vehicles', 'routes', 'drivers'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg transition ${activeTab === tab ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
            {tab === 'dashboard' ? (language === 'hi' ? 'डैशबोर्ड' : 'Dashboard') :
             tab === 'vehicles' ? (language === 'hi' ? 'वाहन' : 'Vehicles') :
             tab === 'routes' ? (language === 'hi' ? 'मार्ग' : 'Routes') :
             (language === 'hi' ? 'ड्राइवर' : 'Drivers')}
          </button>
        ))}
      </div>
      
      {/* Alerts */}
      {data?.alerts?.expiring_vehicle_docs?.length > 0 && activeTab === 'dashboard' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <h3 className="font-medium text-yellow-800 mb-2">⚠️ {language === 'hi' ? 'Document Alerts' : 'Document Alerts'}</h3>
          {data.alerts.expiring_vehicle_docs.map((v, i) => (
            <p key={i} className="text-sm text-yellow-700">Vehicle {v.vehicle_number}: Documents expiring soon</p>
          ))}
        </div>
      )}

      {/* Dashboard Content */}
      {activeTab === 'dashboard' && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <p className="text-sm text-center text-gray-500 mb-2">{language === 'hi' ? 'Capacity Utilization' : 'Capacity Utilization'}: {data?.summary?.utilization_percentage || 0}%</p>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-orange-500 h-3 rounded-full" style={{width: `${data?.summary?.utilization_percentage || 0}%`}}></div>
          </div>
        </div>
      )}

      {/* Vehicles List */}
      {activeTab === 'vehicles' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'वाहन नंबर' : 'Vehicle No.'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'प्रकार' : 'Type'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'क्षमता' : 'Capacity'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'स्थिति' : 'Status'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {vehicles.map((v, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{v.vehicle_number}</td>
                  <td className="px-4 py-3">{v.vehicle_type}</td>
                  <td className="px-4 py-3">{v.seating_capacity}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${v.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {v.status}
                    </span>
                  </td>
                </tr>
              ))}
              {vehicles.length === 0 && <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500">{language === 'hi' ? 'कोई वाहन नहीं' : 'No vehicles'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Routes List */}
      {activeTab === 'routes' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'मार्ग' : 'Route'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'प्रारंभ' : 'Start'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'समाप्त' : 'End'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'स्टॉप' : 'Stops'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {routes.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{r.route_name}</td>
                  <td className="px-4 py-3">{r.start_point}</td>
                  <td className="px-4 py-3">{r.end_point}</td>
                  <td className="px-4 py-3">{r.stops?.length || 0}</td>
                </tr>
              ))}
              {routes.length === 0 && <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500">{language === 'hi' ? 'कोई मार्ग नहीं' : 'No routes'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Drivers List */}
      {activeTab === 'drivers' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'नाम' : 'Name'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'फोन' : 'Phone'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'लाइसेंस' : 'License'}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'स्थिति' : 'Status'}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {drivers.map((d, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{d.name}</td>
                  <td className="px-4 py-3">{d.phone}</td>
                  <td className="px-4 py-3">{d.license_number}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {d.status}
                    </span>
                  </td>
                </tr>
              ))}
              {drivers.length === 0 && <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500">{language === 'hi' ? 'कोई ड्राइवर नहीं' : 'No drivers'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddModal === 'vehicle' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">🚌 {language === 'hi' ? 'नया वाहन जोड़ें' : 'Add New Vehicle'}</h3>
            <div className="space-y-3">
              <input placeholder={language === 'hi' ? 'वाहन नंबर *' : 'Vehicle Number *'} value={formData.vehicle_number || ''} onChange={e => setFormData({...formData, vehicle_number: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <select value={formData.vehicle_type || 'bus'} onChange={e => setFormData({...formData, vehicle_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                <option value="bus">Bus</option>
                <option value="van">Van</option>
                <option value="auto">Auto</option>
              </select>
              <input type="number" placeholder={language === 'hi' ? 'बैठने की क्षमता' : 'Seating Capacity'} value={formData.seating_capacity || ''} onChange={e => setFormData({...formData, seating_capacity: parseInt(e.target.value)})} className="w-full px-4 py-2 border rounded-lg" />
              <div className="flex gap-2">
                <button onClick={() => setShowAddModal(null)} className="flex-1 py-2 bg-gray-200 rounded-lg">{language === 'hi' ? 'रद्द करें' : 'Cancel'}</button>
                <button onClick={handleAddVehicle} disabled={saving} className="flex-1 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50">{saving ? '...' : (language === 'hi' ? 'जोड़ें' : 'Add')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Route Modal */}
      {showAddModal === 'route' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">🛣️ {language === 'hi' ? 'नया मार्ग जोड़ें' : 'Add New Route'}</h3>
            <div className="space-y-3">
              <input placeholder={language === 'hi' ? 'मार्ग का नाम *' : 'Route Name *'} value={formData.route_name || ''} onChange={e => setFormData({...formData, route_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input placeholder={language === 'hi' ? 'प्रारंभ स्थान' : 'Start Point'} value={formData.start_point || ''} onChange={e => setFormData({...formData, start_point: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input placeholder={language === 'hi' ? 'समाप्त स्थान' : 'End Point'} value={formData.end_point || ''} onChange={e => setFormData({...formData, end_point: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <div className="flex gap-2">
                <button onClick={() => setShowAddModal(null)} className="flex-1 py-2 bg-gray-200 rounded-lg">{language === 'hi' ? 'रद्द करें' : 'Cancel'}</button>
                <button onClick={handleAddRoute} disabled={saving} className="flex-1 py-2 bg-green-500 text-white rounded-lg disabled:opacity-50">{saving ? '...' : (language === 'hi' ? 'जोड़ें' : 'Add')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Driver Modal */}
      {showAddModal === 'driver' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">👨‍✈️ {language === 'hi' ? 'नया ड्राइवर जोड़ें' : 'Add New Driver'}</h3>
            <div className="space-y-3">
              <input placeholder={language === 'hi' ? 'नाम *' : 'Name *'} value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input placeholder={language === 'hi' ? 'फोन नंबर *' : 'Phone Number *'} value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input placeholder={language === 'hi' ? 'लाइसेंस नंबर' : 'License Number'} value={formData.license_number || ''} onChange={e => setFormData({...formData, license_number: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <div className="flex gap-2">
                <button onClick={() => setShowAddModal(null)} className="flex-1 py-2 bg-gray-200 rounded-lg">{language === 'hi' ? 'रद्द करें' : 'Cancel'}</button>
                <button onClick={handleAddDriver} disabled={saving} className="flex-1 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-50">{saving ? '...' : (language === 'hi' ? 'जोड़ें' : 'Add')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Leave Management Page (Admin View)

export default TodayAIUsagePage;
